import { Info, Loader2, Lock, RotateCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { PREFERENCE_GROUP_META } from "@/lib/notificationMeta";
import {
  useNotificationPreferences,
  usePutNotificationPreferenceGroup,
} from "@/hooks/useNotificationPreferences";

/**
 * D-043 S2 — Settings → Сповіщення (замінює stub «Скоро», TZ §6.3 + mockup
 * екран 3).
 *
 * Mandatory: завжди locked (AC-010 — engine ігнорує preferences для
 * preferencePolicy=mandatory). Email policy A: для mandatory email керується
 * політикою події (SLA/approval — завжди ON), тому switch locked у warning-
 * тоні. task_assigned email-у не має взагалі (channels=in_app).
 * Configurable: 4 групи (registry preferenceGroup) × (В системі | Email);
 * тогл групи fan-out-иться на CNotificationPreference per eventType.
 */
export function NotificationPreferences() {
  const { data, isPending, isError, refetch } = useNotificationPreferences();
  const put = usePutNotificationPreferenceGroup();

  if (isPending) {
    return (
      <div className="space-y-3" aria-label="Завантаження налаштувань сповіщень">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-2/3" />
            </div>
            <Skeleton className="h-[22px] w-[38px] rounded-full" />
            <Skeleton className="h-[22px] w-[38px] rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="py-4 text-center">
        <p className="mb-3 text-sm text-muted-foreground">
          Не вдалося завантажити налаштування сповіщень
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 rounded-badge border border-primary px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5"
        >
          <RotateCw className="h-3 w-3" />
          Повторити
        </button>
      </div>
    );
  }

  const toggle = (groupKey: string, channel: "in_app" | "email", value: boolean) =>
    put.mutate({ groupKey, channel, value });

  return (
    <div>
      {/* column headers */}
      <div className="flex justify-end gap-9 pb-1 pr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
        <span>В системі</span>
        <span>Email</span>
      </div>

      <div className="divide-y divide-border">
        {/* mandatory — locked */}
        <PrefRow
          title="Призначені задачі та погодження"
          titleExtra={
            <span className="inline-flex items-center gap-1 rounded-full bg-warning px-2 py-px text-[10px] font-bold text-warning-foreground">
              <Lock className="h-2.5 w-2.5" />
              обовʼязкові
            </span>
          }
          sub={
            <>
              призначення · погодження · SLA · уточнення — в системі не вимикаються;{" "}
              <b>email лише для критичних/погодження/SLA</b> (не на кожну задачу)
            </>
          }
          inApp={
            <Switch checked locked aria-label="В системі — обовʼязково, не вимикається" />
          }
          email={
            <Switch
              checked
              locked
              tone="warning"
              aria-label="Email — за політикою події (SLA та погодження завжди з email)"
            />
          }
        />

        {/* configurable groups */}
        {data.configurable.map((g) => {
          const meta = PREFERENCE_GROUP_META[g.groupKey] ?? {
            title: g.groupKey,
            sub: g.eventTypes.join(" · "),
          };

          return (
            <PrefRow
              key={g.groupKey}
              title={meta.title}
              sub={meta.sub}
              inApp={
                <Switch
                  checked={g.inApp}
                  disabled={put.isPending}
                  onCheckedChange={(v) => toggle(g.groupKey, "in_app", v)}
                  aria-label={`${meta.title} — сповіщення в системі`}
                />
              }
              email={
                <Switch
                  checked={g.email}
                  disabled={put.isPending}
                  onCheckedChange={(v) => toggle(g.groupKey, "email", v)}
                  aria-label={`${meta.title} — email-сповіщення`}
                />
              }
            />
          );
        })}
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-card border border-border bg-muted/50 px-3.5 py-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span>
          Обовʼязкові сповіщення (призначення, погодження, SLA, безпека) вимкнути не можна.
          Зведення приходить раз на день о 09:00 і не затримує термінових подій.
        </span>
      </div>

      {put.isPending && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Зберігаємо…
        </div>
      )}
      {put.isError && (
        <p className="mt-2 text-xs text-destructive">
          Не вдалося зберегти — спробуйте ще раз.
        </p>
      )}
    </div>
  );
}

function PrefRow({
  title,
  titleExtra,
  sub,
  inApp,
  email,
}: {
  title: string;
  titleExtra?: React.ReactNode;
  sub: React.ReactNode;
  inApp: React.ReactNode;
  email: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3.5 py-3.5 first:pt-1">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {title}
          {titleExtra}
        </div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <div className="flex shrink-0 items-center gap-9 pr-1">
        {inApp}
        {email}
      </div>
    </div>
  );
}
